import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useEncounter } from '../contexts/EncounterContext';
import { Button } from '../components/ui/button';
import StatusChip from '../components/shared/StatusChip';
import {
  Stethoscope, Clock, RefreshCw, ClipboardList,
  CheckCircle2, UserCheck, Activity, ListOrdered
} from 'lucide-react';
import { toast } from 'sonner';

// ── Status config ────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  scheduled:          { label: 'Scheduled',          color: 'bg-slate-100 text-slate-600',    dot: 'bg-slate-400'   },
  arrived:            { label: 'Arrived',             color: 'bg-yellow-100 text-yellow-700',  dot: 'bg-yellow-400'  },
  checking_in:        { label: 'Checking In',         color: 'bg-yellow-100 text-yellow-700',  dot: 'bg-yellow-500'  },
  ready_for_nurse:    { label: 'Ready for Nurse',     color: 'bg-blue-100 text-blue-700',      dot: 'bg-blue-500'    },
  in_triage:          { label: 'In Triage',           color: 'bg-blue-100 text-blue-700',      dot: 'bg-blue-600 animate-pulse' },
  ready:              { label: 'Ready for Provider',  color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  ready_for_provider: { label: 'Ready for Provider',  color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  in_visit:           { label: 'In Visit',            color: 'bg-primary/10 text-primary',      dot: 'bg-primary animate-pulse' },
  nurse_tasks_pending:{ label: 'Nurse Tasks Pending', color: 'bg-orange-100 text-orange-700',  dot: 'bg-orange-500 animate-pulse' },
  note_signed:        { label: 'Note Signed',         color: 'bg-teal-100 text-teal-700',      dot: 'bg-teal-500'    },
  ready_for_checkout: { label: 'Ready for Checkout',  color: 'bg-purple-100 text-purple-700',  dot: 'bg-purple-500'  },
  completed:          { label: 'Completed',           color: 'bg-slate-100 text-slate-400',    dot: 'bg-slate-300'   },
  checked_out:        { label: 'Checked Out',         color: 'bg-slate-100 text-slate-400',    dot: 'bg-slate-300'   },
  cancelled:          { label: 'Cancelled',           color: 'bg-red-100 text-red-400',        dot: 'bg-red-300'     },
  no_show:            { label: 'No Show',             color: 'bg-red-100 text-red-400',        dot: 'bg-red-400'     },
};

const ACTIVE_NURSE_STATUSES = new Set([
  'ready_for_nurse', 'in_triage', 'ready', 'ready_for_provider',
  'tasks_pending', 'nurse_tasks_pending',
]);
const ACTIVE_PROVIDER_STATUSES = new Set([
  'ready_for_provider', 'in_visit', 'ready',
]);
const DONE_STATUSES = new Set([
  'completed', 'checked_out', 'note_signed', 'ready_for_checkout',
]);

function statusCfg(status) {
  return STATUS_CONFIG[status] || { label: status, color: 'bg-slate-100 text-slate-500', dot: 'bg-slate-400' };
}

// ── Main component ───────────────────────────────────────────────────────────
export default function ProviderQueue() {
  const navigate   = useNavigate();
  const { user }   = useAuth();
  const { startEncounter } = useEncounter();
  const isNurse    = user?.role === 'nurse';

  const [tab, setTab]             = useState('active');   // 'active' | 'schedule'
  const [appointments, setAppts]  = useState([]);
  const [loading, setLoading]     = useState(true);
  const [starting, setStarting]   = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const res = await api.get('/appointments/today');
      setAppts(res.data.appointments || []);
    } catch {
      toast.error('Failed to load appointments');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Derived lists ──────────────────────────────────────────────────────────
  const active = appointments.filter(a =>
    isNurse ? ACTIVE_NURSE_STATUSES.has(a.status) : ACTIVE_PROVIDER_STATUSES.has(a.status)
  );
  // Today's schedule: all appointments ordered by time
  const schedule = [...appointments].sort((a, b) => (a.time || '').localeCompare(b.time || ''));
  const doneCount = appointments.filter(a => DONE_STATUSES.has(a.status)).length;

  // ── Start visit handler ───────────────────────────────────────────────────
  const handleStartVisit = async (appt) => {
    if (appt.status === 'in_visit' && appt.encounter_id) {
      try {
        const res = await api.get(`/encounters/${appt.encounter_id}`);
        startEncounter(res.data.encounter);
        navigate(`/visit/${appt.encounter_id}`);
      } catch { toast.error('Failed to resume visit'); }
      return;
    }
    setStarting(appt.id);
    try {
      const res = await api.post('/encounters/start', { appointment_id: appt.id });
      startEncounter(res.data.encounter);
      navigate(`/visit/${res.data.encounter.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start visit');
    } finally { setStarting(null); }
  };

  // ── Appointment card ───────────────────────────────────────────────────────
  const AppointmentCard = ({ appt, showAction = true }) => {
    const cfg    = statusCfg(appt.status);
    const done   = DONE_STATUSES.has(appt.status);
    const canStart = isNurse
      ? ACTIVE_NURSE_STATUSES.has(appt.status)
      : ACTIVE_PROVIDER_STATUSES.has(appt.status);

    return (
      <div className={`rounded-xl border transition-all ${done ? 'bg-slate-50 border-slate-100 opacity-60' : 'bg-white border-slate-200 hover:shadow-md'}`}
        data-testid={`queue-card-${appt.id}`}>
        <div className="flex items-center gap-3 px-4 py-3">
          {/* Status dot */}
          <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${cfg.dot}`} />

          {/* Patient info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className={`text-sm font-semibold truncate ${done ? 'text-slate-400' : 'text-slate-800'}`}>
                {appt.patient_name || 'Unknown Patient'}
              </p>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${cfg.color}`}>
                {cfg.label}
              </span>
            </div>
            <div className="flex items-center gap-3 mt-0.5 flex-wrap">
              <span className="text-[11px] text-slate-400 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {appt.time || '—'}
              </span>
              {appt.visit_type && (
                <span className="text-[11px] text-slate-400">{appt.visit_type}</span>
              )}
              {appt.room && (
                <span className="text-[11px] text-slate-400">Room {appt.room}</span>
              )}
              {appt.provider_name && (
                <span className="text-[11px] text-slate-400">
                  {appt.provider_name.split(' ').slice(-1)[0]}
                </span>
              )}
            </div>
          </div>

          {/* Action */}
          {showAction && !done && canStart && (
            <Button size="sm"
              className={`shrink-0 h-8 text-xs gap-1.5 ${appt.status === 'in_visit' ? 'bg-amber-600 hover:bg-amber-700' : ''}`}
              disabled={starting === appt.id}
              onClick={() => handleStartVisit(appt)}
              data-testid={`start-visit-${appt.id}`}>
              <Stethoscope className="w-3.5 h-3.5" />
              {appt.status === 'in_visit' ? 'Resume Visit' : 'Start Visit'}
            </Button>
          )}
          {done && (
            <CheckCircle2 className="w-4 h-4 text-slate-300 shrink-0" />
          )}
        </div>
      </div>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="p-4 sm:p-6 max-w-3xl animate-fadeIn">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
            {isNurse ? 'Nurse Queue' : 'Provider Queue'}
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            {active.length} active · {doneCount} completed today
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchAll} className="gap-1 h-8 text-xs">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 bg-slate-100 p-1 rounded-xl w-fit">
        <button
          onClick={() => setTab('active')}
          className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-semibold transition-all
            ${tab === 'active' ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}
          data-testid="tab-active-queue">
          <Activity className="w-3.5 h-3.5" />
          Active Queue
          {active.length > 0 && (
            <span className="ml-0.5 bg-primary text-primary-foreground text-[10px] px-1.5 py-0.5 rounded-full font-bold">
              {active.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setTab('schedule')}
          className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-semibold transition-all
            ${tab === 'schedule' ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}
          data-testid="tab-schedule">
          <ListOrdered className="w-3.5 h-3.5" />
          Today's Schedule
          <span className="ml-0.5 text-[10px] text-slate-400 font-normal">{appointments.length}</span>
        </button>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-16 bg-muted rounded-xl animate-pulse" />
          ))}
        </div>
      ) : tab === 'active' ? (

        /* ── ACTIVE QUEUE TAB ── */
        <div className="space-y-2" data-testid="active-queue-list">
          {active.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <UserCheck className="w-10 h-10 text-slate-200 mb-3" />
              <p className="text-sm font-medium text-slate-500">Queue is clear</p>
              <p className="text-xs text-slate-400 mt-1">
                No active patients right now · {doneCount} seen today
              </p>
              {doneCount > 0 && (
                <button onClick={() => setTab('schedule')}
                  className="mt-3 text-xs text-primary underline underline-offset-2">
                  View Today's Schedule →
                </button>
              )}
            </div>
          ) : (
            active.map(appt => (
              <AppointmentCard key={appt.id} appt={appt} showAction />
            ))
          )}
        </div>

      ) : (

        /* ── TODAY'S SCHEDULE TAB ── */
        <div className="space-y-1.5" data-testid="schedule-list">
          {schedule.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <ClipboardList className="w-10 h-10 text-slate-200 mb-3" />
              <p className="text-sm font-medium text-slate-500">No appointments today</p>
            </div>
          ) : (
            <>
              {/* Summary row */}
              <div className="flex gap-3 mb-3 flex-wrap">
                {[
                  { label: 'Total', count: appointments.length, color: 'text-slate-600' },
                  { label: 'Active', count: active.length, color: 'text-emerald-600' },
                  { label: 'Done', count: doneCount, color: 'text-slate-400' },
                  { label: 'Remaining', count: appointments.length - doneCount - active.length, color: 'text-amber-600' },
                ].map(s => (
                  <div key={s.label} className="flex items-center gap-1">
                    <span className={`text-sm font-bold ${s.color}`}>{s.count}</span>
                    <span className="text-[10px] text-slate-400">{s.label}</span>
                  </div>
                ))}
              </div>
              {schedule.map(appt => (
                <AppointmentCard key={appt.id} appt={appt}
                  showAction={!DONE_STATUSES.has(appt.status)} />
              ))}
            </>
          )}
        </div>

      )}
    </div>
  );
}
